import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import SectionHeader from '@/components/Common/SectionHeader';
import {fadeInUp} from '@/animation/scroll-animation';
import {borderRadius, borderWidth, colors, fontSize, lineHeight, transition} from '@/styles/tokens';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ReadinessChecklistProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** Readiness signals, rendered as two columns of check-marked pills. */
    items: string[];
    /** Optional CTA below the list. Omit both to render the list alone. */
    buttonText?: string;
    onButtonClick?: () => void;
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

/** Double check mark — the same motif used by CTAGrid, tinted for light surfaces. */
const CheckIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{flexShrink: 0, marginTop: 2}}
        aria-hidden="true"
    >
        <path d="M4 11.5L8.5 16L18 7" stroke={colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M8 11.5L12.5 16L22 7"
            stroke={colors.primary.main}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.45"
        />
    </svg>
);

// ─── Module-level sx constants ────────────────────────────────────────────────

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'},
    gap: {xs: 2, md: 2.5}
} as const;

const fullHeightSx = {height: '100%'} as const;

const itemSx = {
    height: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    px: {xs: 2, md: 2.5},
    py: {xs: 1.75, md: 2},
    bgcolor: colors.background.subtle,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    transition: `background-color ${transition.base}, border-color ${transition.base}`,
    '&:hover': {
        bgcolor: colors.background.tag,
        borderColor: colors.primary.tintBorder
    }
} as const;

const itemTextSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const ctaWrapSx = {display: 'flex', justifyContent: 'center', mt: {xs: 4, md: 5}} as const;

const ctaButtonSx = {whiteSpace: 'nowrap'} as const;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ReadinessChecklist (Organism) — a centered section header above a two-column
 * list of check-marked readiness signals, with an optional CTA below. Use it for
 * "are you ready for X" / qualification blocks on service pages.
 */
const ReadinessChecklist = ({eyebrow, heading, subheading, items, buttonText, onButtonClick}: ReadinessChecklistProps) => (
    <Box component="section">
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={parse(heading)} subheading={subheading ? parse(subheading) : undefined} />
            <Box sx={gridSx}>
                {items.map((item, index) => (
                    <AnimatedComponent key={item} variants={fadeInUp} delay={index * 0.1} style={fullHeightSx}>
                        <Box sx={itemSx}>
                            <CheckIcon />
                            <Typography component="p" className="font-regular-text remove-underline" sx={itemTextSx}>
                                {parse(item)}
                            </Typography>
                        </Box>
                    </AnimatedComponent>
                ))}
            </Box>
            {buttonText && (
                <Box sx={ctaWrapSx}>
                    <MUIButton
                        size="medium"
                        variant="contained"
                        disableRipple={true}
                        className="animated-button button-bg-primary"
                        label={buttonText}
                        aria-label={buttonText}
                        endIcon={<ArrowForwardIcon />}
                        onClick={onButtonClick}
                        sx={ctaButtonSx}
                    />
                </Box>
            )}
        </MUIContainer>
    </Box>
);

export default memo(ReadinessChecklist);
